import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./RouteHistoryListDetail.css";

import PageHeader from "../../../components/common/PageHeader/PageHeader";
import CloseButton2 from "../../../components/common/CloseButton/CloseButton2";
import InputBoxSmall from "../../../components/common/InputBox/InputBoxSmall";
import SimpleCheckbox from "../../../components/common/CheckBox/SimpleCheckbox";

import { RouteHistoryDetailApi } from "../../../services/routeHistoryApis";
import { formatDate } from "../../../utils/dates";
import { useTranslation } from "react-i18next";
import PreviewImage from "../../../components/popup/ImagePreview/ImagePreview";

const RouteHistoryListDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [customerVisits, setCustomerVisits] = useState([]);
  const [historyInfo, setHistoryInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchHistoryDetail();
    }
  }, [id]);

  async function fetchHistoryDetail() {
    try {
      const res = await RouteHistoryDetailApi(id);

      if (res.data.success) {
        const data = res.data.data;
        setHistoryInfo(data);
        setCustomerVisits(data.customers || []);
      }
    } catch (err) {
      if (err?.response?.data?.error_code === 1001) navigate("/error");
    }
  }

  const formatTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");

    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;

    return `${hours}.${minutes} ${ampm}`;
  };

  const formatDuration = (value) => {
    if (!value) return "-";

    const parts = value.split(":"); // [HH, MM, SS.micro]

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseFloat(parts[2]);

    if (hours > 0) return `${hours} hr`;
    if (minutes > 0) return `${minutes} min`;

    return `${Math.floor(seconds)} sec`;
  };

  return (
    <div className="whole-container">
      {/* HEADER */}
      <div className="path-header">
        <PageHeader
          breadcrumbs={[
            { label: "Route Management" },
            { label: "Route History", path: "/routes/history" },
            { label: "Details" },
          ]}
        />
      </div>

      {/* TOP BAR */}

      <div className="page-actions-bar">
        <div className="name-utility">
          <span className="title-heading">{t("History")}</span>
        </div>

        <div className="page-name-rightside-icons">
          <CloseButton2 onClick={() => navigate("/routes/history")} />
        </div>
      </div>

      <div className="route-history-detail route">
        {/* ===== TOP STATS ===== */}
        <div className="stats-container">
          {/* VISITED */}
          <div className="stat-card visited ">
            <div className="stat-icon route-green">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                className="route-icon "
              >
                <path
                  d="M21.8011 9.99999C22.2578 12.2413 21.9323 14.5714 20.879 16.6018C19.8256 18.6322 18.108 20.24 16.0126 21.1573C13.9172 22.0746 11.5707 22.2458 9.3644 21.6424C7.15807 21.0389 5.22529 19.6974 3.88838 17.8414C2.55146 15.9854 1.89122 13.7272 2.01776 11.4434C2.14431 9.15952 3.04998 6.98808 4.58375 5.29116C6.11752 3.59424 8.18668 2.47442 10.4462 2.11844C12.7056 1.76247 15.0189 2.19185 17.0001 3.33499"
                  stroke="#00A63E"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 11L12 14L22 4"
                  stroke="#00A63E"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-title">{t("Visited")}</span>
              <span className="stat-value">
                {historyInfo?.total_visited} / {historyInfo?.total_customers}
              </span>
            </div>
          </div>

          {/* MISSED */}
          <div className="stat-card missed">
            <div className="stat-icon route-red">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                className="route-icon "
              >
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="#E7000B"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 8V12"
                  stroke="#E7000B"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 16H12.01"
                  stroke="#E7000B"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-title">{t("Missed Visits")}</span>
              <span className="stat-value">{historyInfo?.missed_visits}</span>
            </div>
          </div>

          {/* ORDERS */}
          <div className="stat-card orders">
            <div className="stat-icon route-blue">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                className="route-icon "
              >
                <path
                  d="M8 22C8.55228 22 9 21.5523 9 21C9 20.4477 8.55228 20 8 20C7.44772 20 7 20.4477 7 21C7 21.5523 7.44772 22 8 22Z"
                  stroke="#155DFC"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M19 22C19.5523 22 20 21.5523 20 21C20 20.4477 19.5523 20 19 20C18.4477 20 18 20.4477 18 21C18 21.5523 18.4477 22 19 22Z"
                  stroke="#155DFC"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2.0498 2.05005H4.0498L6.7098 14.47C6.80738 14.9249 7.06048 15.3315 7.42552 15.6199C7.79056 15.9083 8.24471 16.0604 8.7098 16.05H18.4898C18.945 16.0493 19.3863 15.8933 19.7408 15.6079C20.0954 15.3224 20.3419 14.9246 20.4398 14.48L22.0898 7.05005H5.1198"
                  stroke="#155DFC"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-title">{t("Orders Placed")}</span>
              <span className="stat-value">
                {historyInfo?.total_orders_placed}
              </span>
            </div>
          </div>

          {/* TIME */}
          <div className="stat-card time">
            <div className="stat-icon route-gray">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                className="route-icon "
              >
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="#4A5565"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 6V12L16 14"
                  stroke="#4A5565"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-title">{t("Average Visit Time")}</span>
              <span className="stat-value">
                {formatDuration(historyInfo?.average_visit_duration)}
              </span>
            </div>
          </div>
        </div>

        {/* ===== INFO ROW ===== */}
        <div className="route-info-container">
          <InputBoxSmall
            readOnly
            label="Route Name"
            defaultValue={historyInfo?.route_name}
          />

          <InputBoxSmall
            readOnly
            label="Sales Person"
            defaultValue={historyInfo?.sales_person_name}
          />

          <InputBoxSmall
            readOnly
            label="Date"
            defaultValue={formatDate(historyInfo?.date)}
          />
        </div>
      </div>

      <div className="table-container route-history-table">
        <table className="common-table">
          <thead className="common-table-head">
            <tr className="common-table-thead-tr">
              <th>{t("Customer")}</th>
              <th>{t("Visited")}</th>
              <th>{t("Order Placed")}</th>
              <th>{t("Latitude")}</th>
              <th>{t("Longitude")}</th>
              <th>{t("Check In Time")}</th>
              <th>{t("Check Out Time")}</th>
              <th>{t("Duration")}</th>
              <th>{t("Reason")}</th>
              <th>{t("Image")}</th>
              <th />
            </tr>
          </thead>

          <tbody className="common-table-tbody">
            {customerVisits.map((row) => (
              <tr key={row.id}>
                <td className="capitalize">{row.customer_name}</td>

                <td>
                  <SimpleCheckbox checked={row.visited} disabled />
                </td>

                <td>
                  <SimpleCheckbox checked={row.order_placed} disabled />
                </td>

                <td>{row.latitude}</td>
                <td>{row.longitude}</td>
                <td>{formatTime(row.check_in_time)}</td>
                <td>{formatTime(row.check_out_time)}</td>
                <td>{formatDuration(row.duration)}</td>
                <td>{row.reason}</td>
                <td>
                  {row.image ? (
                    <PreviewImage
                      src={row.image}
                      alt={`Visit image – ${row.customer_name}`}
                    />
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RouteHistoryListDetail;
